(function () {
  const validatedForms = new WeakSet();

  // --- Helpers ---

  function haversineDistanceMiles(lat1, lng1, lat2, lng2) {
    const R = 3958.8;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function readCookie(name) {
    const prefix = name + '=';
    const entry = document.cookie.split('; ').find((c) => c.startsWith(prefix));
    if (!entry) return null;
    try {
      return JSON.parse(decodeURIComponent(entry.slice(prefix.length)));
    } catch {
      return null;
    }
  }

  function getUserAddress() {
    return readCookie('addressDrawerResult');
  }

  function getProductDeliveryConfig(form) {
    const scope = form.closest('product-info') || form.closest('.shopify-section') || document;
    const script = scope.querySelector(
      'location-delivery-status[data-type="main"] script[type="application/json"]'
    );
    if (!script) return null;
    try {
      return JSON.parse(script.textContent.trim());
    } catch {
      return null;
    }
  }

  // FPS-679 — mirrors supportsLocalDelivery in assets/delivery-stock-status.js. A radius alone
  // does not mean the store delivers; supported_delivery_methods is the flag that does.
  function supportsLocalDelivery(location) {
    const methods = location?.deliveryMethods;
    if (!Array.isArray(methods)) return false;
    return methods.some((method) => String(method).toLowerCase().includes('delivery'));
  }

  function getDeliveryStoreNames(availableLocations) {
    return availableLocations
      .filter((loc) => loc.available && Number(loc.deliveryRadius) > 0 && supportsLocalDelivery(loc))
      .map((loc) => loc.name);
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js');
      return res.ok ? res.json() : null;
    } catch {
      return null;
    }
  }

  const FORM_SELECTOR = "form[is='product-form'], form[ref='main-pdp-form']";

  function findDeliveryForm() {
    for (const form of document.querySelectorAll(FORM_SELECTOR)) {
      if (form.querySelector('input[name="properties[_delivery_method]"]')?.value === 'delivery') {
        return form;
      }
    }
    return null;
  }

  // delivery-options and the form are siblings under .product-form — closest() won't traverse sideways.
  function findFormFromSibling(el) {
    const container =
      el.closest('.product-form') ||
      el.closest('product-info') ||
      el.closest('.shopify-section');
    return container?.querySelector(FORM_SELECTOR) || null;
  }

  // --- Core validation ---

  async function validateDeliveryConflict(form) {
    const userAddress = getUserAddress();
    const config = getProductDeliveryConfig(form);

    const newProductStoreNames =
      config?.availableLocations?.length > 0
        ? getDeliveryStoreNames(config.availableLocations)
        : [];
    const availableStoresStr = newProductStoreNames.join(',');

    if (!userAddress?.latitude || !userAddress?.longitude || !config?.availableLocations?.length) {
      return { valid: true, availableStores: availableStoresStr };
    }

    // Submit-time backstop: no store both stocks this product and delivers, so no cart state can
    // make it deliverable. Checked before the empty-cart shortcut below, which would otherwise let
    // the first such item through (FPS-679).
    if (newProductStoreNames.length === 0) {
      return { valid: false, availableStores: availableStoresStr };
    }

    const cart = await fetchCart();
    if (!cart) return { valid: true, availableStores: availableStoresStr };

    const deliveryItems = cart.items.filter(
      (item) => item.properties?._delivery_method === 'delivery'
    );
    if (deliveryItems.length === 0) return { valid: true, availableStores: availableStoresStr };

    let cartStoreIntersection = null;
    let hasUnverifiedItem = false;
    for (const item of deliveryItems) {
      const stored = (item.properties?._available_delivery_stores || '')
        .split(',')
        .filter(Boolean);
      if (stored.length === 0) {
        // FPS-677 (round 2): a delivery-method cart item with no recorded store set can't be
        // proven compatible with anything -- `continue`d here before, which silently dropped it
        // out of the intersection and let a mismatched second store through unblocked. Since the
        // hard requirement is single-store fulfillment, treat "can't verify" as a conflict
        // instead of "no constraint". Short-circuits via `break` since one unverified item is
        // already enough to fail the whole check below.
        hasUnverifiedItem = true;
        break;
      }
      const itemSet = new Set(stored);
      cartStoreIntersection =
        cartStoreIntersection === null
          ? itemSet
          : new Set([...itemSet].filter((s) => cartStoreIntersection.has(s)));
    }

    if (hasUnverifiedItem) {
      return { valid: false, availableStores: availableStoresStr };
    }

    if (!cartStoreIntersection || cartStoreIntersection.size === 0) {
      return { valid: true, availableStores: availableStoresStr };
    }

    const newProductSet = new Set(newProductStoreNames);
    const commonStoreNames = [...newProductSet].filter((s) => cartStoreIntersection.has(s));

    if (commonStoreNames.length === 0) {
      return { valid: false, availableStores: availableStoresStr };
    }

    const commonLocations = config.availableLocations.filter((loc) =>
      commonStoreNames.includes(loc.name)
    );
    const withinRadius = commonLocations.some((loc) => {
      const storeLat = Number(loc.latlng?.latitude);
      const storeLng = Number(loc.latlng?.longitude);
      if (!storeLat || !storeLng) return false;
      const dist = haversineDistanceMiles(
        userAddress.latitude,
        userAddress.longitude,
        storeLat,
        storeLng
      );
      return dist <= Number(loc.deliveryRadius || 0);
    });

    if (!withinRadius) {
      return { valid: false, availableStores: availableStoresStr };
    }

    return { valid: true, availableStores: availableStoresStr };
  }

  // --- Side effects ---

  function injectAvailableStores(form, storesStr) {
    if (!storesStr) return;
    form.querySelector('input[name="properties[_available_delivery_stores]"]')?.remove();
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_available_delivery_stores]';
    input.value = storesStr;
    form.appendChild(input);
  }

  function setAddressStatus(el, msg) {
    if (!el) return;
    el.innerHTML = msg;
    el.classList.remove('valid');
    el.classList.add('invalid');
  }

  function showConflictError(form) {
    const config = getProductDeliveryConfig(form);
    const msg = config?.texts?.locationNotAvailable || 'Delivery not available in your area';

    // Directly update every addressStatus ref on the page (PDP panel + address drawer).
    // Using direct DOM writes instead of relying on event order, since delivery-stock-status.js
    // can re-render after our dispatch and overwrite the status back to "available". Stash the
    // pre-conflict text/class (once) so clearConflictError() can restore it later -- don't
    // overwrite an existing stash with this conflict text itself.
    document.querySelectorAll('location-delivery-status [ref="addressStatus"]').forEach((el) => {
      if (el.dataset.conflictGatePrevHtml === undefined) {
        el.dataset.conflictGatePrevHtml = el.innerHTML;
        el.dataset.conflictGatePrevClass = el.className;
      }
      setAddressStatus(el, msg);
    });

    // Dispatch with available:false to disable ATC via ProductInfo.handleToggleATC.
    document.dispatchEvent(
      new CustomEvent('deliveryAddress:statusUpdated', {
        detail: { msg, className: 'invalid', available: false },
      })
    );
  }

  // Restores whatever addressStatus showed before showConflictError() last overwrote it, for
  // when a newer, authoritative check finds no conflict after a stale one already flagged one.
  // Direct DOM restore (not a re-render) so this can't loop back into re-triggering the eager
  // checks below -- the re-enable dispatch is tagged so Trigger 1 knows to ignore its own echo.
  function clearConflictError() {
    document.querySelectorAll('location-delivery-status [ref="addressStatus"]').forEach((el) => {
      if (el.dataset.conflictGatePrevHtml === undefined) return;
      el.innerHTML = el.dataset.conflictGatePrevHtml;
      el.className = el.dataset.conflictGatePrevClass;
      delete el.dataset.conflictGatePrevHtml;
      delete el.dataset.conflictGatePrevClass;
    });

    document.dispatchEvent(
      new CustomEvent('deliveryAddress:statusUpdated', {
        detail: { available: true, __conflictGateEcho: true },
      })
    );
  }

  // --- Eager validation ---

  // Trigger 1 (stale-cookie read) and Trigger 1b (fresh, post-confirm) both fire off the same
  // address change, with Trigger 1 always starting first. If Trigger 1's stale check resolves
  // invalid before Trigger 1b's fresh check even starts, showConflictError() runs first -- and
  // since the old code never had a "valid" branch, that stale error was never cleared once the
  // fresh check came back valid. Only the most recently STARTED check is allowed to act, and it
  // now acts on BOTH outcomes so a valid verdict actively clears any conflict a stale one showed.
  let conflictCheckSeq = 0;

  async function runEagerConflictCheck(form) {
    const seq = ++conflictCheckSeq;
    const result = await validateDeliveryConflict(form);
    if (seq !== conflictCheckSeq) return;
    if (!result.valid) {
      showConflictError(form);
    } else {
      clearConflictError();
    }
  }

  // Trigger 1: delivery address status updated to "available" (address set + product in stock).
  // Runs in capture phase so it fires before ProductInfo.handleToggleATC (bubble phase),
  // allowing us to immediately re-block if a conflict is still active.
  document.addEventListener('deliveryAddress:statusUpdated', async function (event) {
    const { available, __conflictGateEcho } = event.detail || {};
    // Ignore our own re-enable dispatch from clearConflictError() -- otherwise this listener
    // would react to it, run another check, and clearConflictError() would dispatch again.
    if (__conflictGateEcho || !available) return;

    const form = findDeliveryForm();
    if (!form) return;

    await runEagerConflictCheck(form);
  }, true);

  // Trigger 1b: address freshly confirmed via the drawer. statusUpdated (above) can fire
  // before address-drawer.js finishes writing the addressDrawerResult cookie -- if this
  // listener's own runEagerConflictCheck (which reads that cookie via getUserAddress())
  // wins that race, it validates against the PREVIOUS address and only self-corrects on
  // the next submit. deliveryAddress:confirmed is dispatched after the cookie write
  // (address-drawer.js's handleConfirmAddress), so it's always safe to read here.
  document.addEventListener('deliveryAddress:confirmed', async function () {
    const form = findDeliveryForm();
    if (!form) return;

    await runEagerConflictCheck(form);
  }, true);

  // Trigger 2: user switches to delivery radio when address is already set.
  document.addEventListener('change', async function (event) {
    const radio = event.target?.closest?.('input[name^="delivery-option"]');
    if (!radio || radio.value !== 'delivery') return;

    // cart-method-lock (FPS-682) restoring the cart's existing method on load is not the buyer
    // switching to delivery. Checking here would validate a product that is not in the cart, on
    // every PDP load in a delivery cart, and can surface a conflict error for a valid cart.
    if (event.cartMethodLockRestore) return;

    const userAddress = getUserAddress();
    if (!userAddress?.latitude || !userAddress?.longitude) return;

    const form = findFormFromSibling(radio);
    if (!form) return;

    await runEagerConflictCheck(form);
  });

  // --- Submit interceptor (safety net + inject _available_delivery_stores) ---

  document.addEventListener(
    'submit',
    async function (event) {
      const form = event.target;
      if (!form.matches('form[is="product-form"]')) return;
      if (form.querySelector('input[name="properties[_delivery_method]"]')?.value !== 'delivery') return;

      if (validatedForms.has(form)) {
        validatedForms.delete(form);
        return;
      }

      const submitter = event.submitter;
      event.preventDefault();
      event.stopImmediatePropagation();

      const result = await validateDeliveryConflict(form);

      if (result.valid) {
        if (result.availableStores) injectAvailableStores(form, result.availableStores);
        validatedForms.add(form);
        form.requestSubmit(submitter || undefined);
      } else {
        showConflictError(form);
      }
    },
    true
  );
})();
